// Content for the three cards on the workflow landing page (`/`).
// PE/IB bullet counts come from GET /api/landing/stats (Redis-cached on the server).
// FALLBACK_LANDING_STATS is shown until that query returns.
import { Briefcase } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import type { ScoutVertical } from '@/components/layout/Sidebar/sidebarNav';

export type WorkflowCardTone = 'indigo' | 'amber' | 'pink';

export interface LandingPeStats {
  firms: number;
  holdings: number;
  people: number;
}

export interface LandingIbStats {
  banks: number;
  transactions: number;
  people: number;
}

export interface LandingStats {
  pe: LandingPeStats;
  ib: LandingIbStats;
}

/** Last known Figma-era totals; used until /api/landing/stats responds. */
export const FALLBACK_LANDING_STATS: LandingStats = {
  pe: { firms: 1384, holdings: 35550, people: 20744 },
  ib: { banks: 4157, transactions: 11839, people: 21207 },
};

export interface WorkflowCardSpec {
  workflow: ScoutVertical;
  title: string;
  icon: LucideIcon;
  /** Tints the icon tile — indigo / amber / pink, per the Figma. */
  tone: WorkflowCardTone;
  blurb: string;
  bullets: string[];
  /** When true the card is always disabled and shows a Coming Soon pill. */
  comingSoon?: boolean;
}

export function formatLandingCount(n: number): string {
  return n.toLocaleString('en-US');
}

export function peCardBullets(stats: LandingPeStats): string[] {
  return [
    `${formatLandingCount(stats.firms)} PE firms tracked`,
    `${formatLandingCount(stats.holdings)} portfolio holdings`,
    `${formatLandingCount(stats.people)} professionals`,
    'Criteria screener & activity feed',
  ];
}

export function ibCardBullets(stats: LandingIbStats): string[] {
  return [
    `${formatLandingCount(stats.banks)} banks tracked`,
    `${formatLandingCount(stats.transactions)} deal transactions`,
    `${formatLandingCount(stats.people)} professionals`,
    'Screener & deal filters',
  ];
}

/**
 * Left-to-right order as drawn in the Figma. Note this deliberately differs from the sidebar
 * pill's IB-first `WORKFLOW_ORDER` — the two screens really are ordered differently in the design.
 */
export const WORKFLOW_CARDS: WorkflowCardSpec[] = [
  {
    workflow: 'research',
    title: 'Buyer & Target Research',
    icon: Briefcase,
    tone: 'indigo',
    blurb:
      'Turn a mandate into a scored list of financial buyers, strategic acquirers, or sell-side targets.',
    bullets: [
      'Financial buyer, strategic & target lists',
      'AI fit scoring & rationale',
      'Apollo, Coresignal, Maps & DataMagnet',
      'CRM comments, export & knowledge bank',
    ],
  },
  {
    workflow: 'pe',
    title: 'Private Equity',
    icon: Briefcase,
    tone: 'amber',
    blurb: 'Track portfolio firms, screen buyout targets, monitor the team.',
    bullets: peCardBullets(FALLBACK_LANDING_STATS.pe),
  },
  {
    workflow: 'ib',
    title: 'Investment Banking',
    icon: Briefcase,
    tone: 'pink',
    blurb: 'Build your coverage universe. Track deal activity across banks.',
    bullets: ibCardBullets(FALLBACK_LANDING_STATS.ib),
    comingSoon: true,
  },
];
